import {
  ACTIVE_ABILITY_DEFINITIONS,
  BALL_CORE_DEFINITIONS,
  BALL_FUSION_DEFINITIONS,
  BOSS_DEFINITION,
  BRICK_DEFINITIONS,
  BUILD_DEFINITIONS,
  ELITE_WAVE_DEFINITIONS,
  GAMEPLAY_CONFIG,
  getBossEncounterDefinition,
  RELIC_DEFINITIONS,
  RISK_CONTRACT_DEFINITIONS,
  RUN_CLEANSE_DEFINITION,
  RUN_CURSE_DEFINITIONS,
  RUN_EVENT_DEFINITIONS,
  UPGRADE_DEFINITIONS,
} from '@brick-rogue/game-content';
import { CYBERPUNK_VISUAL_ASSETS } from '@brick-rogue/game-content/visual-assets';
import { useState } from 'react';

import { AppHeader } from './AppHeader';
import { useReducedMotionPreference } from '../features/settings/uiPreferencesStore';

type CodexCategory =
  'bricks' | 'upgrades' | 'cores' | 'relics' | 'builds' | 'abilities' | 'operations' | 'boss';

const CATEGORIES: readonly {
  readonly id: CodexCategory;
  readonly label: string;
}[] = [
  { id: 'bricks', label: '砖块' },
  { id: 'upgrades', label: '强化' },
  { id: 'cores', label: '球芯融合' },
  { id: 'relics', label: '遗物' },
  { id: 'builds', label: '构筑' },
  { id: 'abilities', label: '技能' },
  { id: 'operations', label: '事件与规则' },
  { id: 'boss', label: 'Boss' },
];

const OPERATION_ITEMS = [
  ...Object.values(RUN_EVENT_DEFINITIONS).map((event) => ({
    id: `event-${event.id}`,
    label: 'SEEDED EVENT',
    name: event.name,
    description: `${event.description} ${event.costLabel}；${event.rewardLabel}。`,
    imageUrl: CYBERPUNK_VISUAL_ASSETS.events[event.id],
  })),
  ...Object.values(RUN_CURSE_DEFINITIONS).map((curse) => ({
    id: `curse-${curse.id}`,
    label: curse.source === 'relic' ? 'RELIC CURSE' : 'EVENT CURSE',
    name: curse.name,
    description: `${curse.description} ${curse.riskLabel}；${curse.rewardLabel}。`,
    imageUrl:
      curse.id === 'reactor-instability'
        ? CYBERPUNK_VISUAL_ASSETS.relics['cursed-reactor']
        : CYBERPUNK_VISUAL_ASSETS.events['glass-overclock'],
  })),
  {
    id: RUN_CLEANSE_DEFINITION.id,
    label: 'PURIFICATION NODE',
    name: RUN_CLEANSE_DEFINITION.name,
    description: `${RUN_CLEANSE_DEFINITION.description} ${RUN_CLEANSE_DEFINITION.costLabel}；${RUN_CLEANSE_DEFINITION.rewardLabel}。`,
    imageUrl: CYBERPUNK_VISUAL_ASSETS.cleanse[RUN_CLEANSE_DEFINITION.id],
  },
  ...Object.values(ELITE_WAVE_DEFINITIONS).map((elite) => ({
    id: `elite-${elite.id}`,
    label: 'ELITE PROTOCOL',
    name: elite.name,
    description: `${elite.description} ${elite.riskLabel}；${elite.rewardLabel}。`,
    imageUrl:
      elite.id === 'fortress-matrix'
        ? CYBERPUNK_VISUAL_ASSETS.gameplay.bricks.reinforced
        : elite.id === 'overclock-storm'
          ? CYBERPUNK_VISUAL_ASSETS.upgrades['overclock-drive']
          : CYBERPUNK_VISUAL_ASSETS.ballCores.phase,
  })),
  ...Object.values(RISK_CONTRACT_DEFINITIONS).map((contract) => ({
    id: `contract-${contract.id}`,
    label: 'RISK CONTRACT',
    name: contract.name,
    description: `${contract.description} ${contract.riskLabel}；${contract.rewardLabel}。`,
    imageUrl:
      contract.id === 'siege-bounty'
        ? CYBERPUNK_VISUAL_ASSETS.gameplay.bricks.armored
        : contract.id === 'redline-license'
          ? CYBERPUNK_VISUAL_ASSETS.upgrades['overclock-drive']
          : CYBERPUNK_VISUAL_ASSETS.ballCores.gravity,
  })),
] as const;

export function CodexPage() {
  const [category, setCategory] = useState<CodexCategory>('bricks');
  const reducedMotion = useReducedMotionPreference();

  return (
    <div className="portal-shell" data-motion={reducedMotion ? 'reduced' : 'full'}>
      <a className="skip-link" href="#codex-content">
        跳到图鉴内容
      </a>
      <AppHeader />
      <main id="codex-content" className="content-page">
        <header className="content-page__heading">
          <p className="section-kicker">TACTICAL ARCHIVE</p>
          <h1>战术图鉴</h1>
          <p>所有战斗对象均由数据定义；后续内容可以直接扩展，而无需改写战斗框架。</p>
        </header>

        <div className="codex-tabs" role="tablist" aria-label="图鉴分类">
          {CATEGORIES.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={category === item.id}
              aria-controls="codex-panel"
              onClick={() => setCategory(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>

        <section id="codex-panel" className="codex-grid" role="tabpanel" tabIndex={0}>
          {category === 'bricks'
            ? Object.values(BRICK_DEFINITIONS).map((brick) => (
                <article key={brick.id} className="codex-card" data-accent={brick.id}>
                  <div className="codex-card__symbol codex-brick" aria-hidden="true">
                    <img src={CYBERPUNK_VISUAL_ASSETS.gameplay.bricks[brick.id]} alt="" />
                  </div>
                  <p>BRICK · HP {brick.hitPoints}</p>
                  <h2>{brick.name}</h2>
                  <span>{brick.description}</span>
                  <small>基础分数 {brick.score}</small>
                </article>
              ))
            : null}

          {category === 'upgrades'
            ? Object.values(UPGRADE_DEFINITIONS).map((upgrade) => (
                <article key={upgrade.id} className="codex-card">
                  <img
                    className="codex-card__image"
                    src={CYBERPUNK_VISUAL_ASSETS.upgrades[upgrade.id]}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                  />
                  <p>UPGRADE · MAX {upgrade.maxStacks}</p>
                  <h2>{upgrade.name}</h2>
                  <span>{upgrade.description}</span>
                  <small>效果值 {upgrade.magnitude}</small>
                </article>
              ))
            : null}

          {category === 'cores'
            ? [
                ...Object.values(BALL_CORE_DEFINITIONS).map((core) => (
                  <article key={core.id} className="codex-card" data-accent={core.id}>
                    <img
                      className="codex-card__image"
                      src={CYBERPUNK_VISUAL_ASSETS.ballCores[core.id]}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                    />
                    <p>BALL CORE</p>
                    <h2>{core.name}球芯</h2>
                    <span>{UPGRADE_DEFINITIONS[core.upgradeId].description}</span>
                    <small>解锁后可参与双球芯融合</small>
                  </article>
                )),
                ...Object.values(BALL_FUSION_DEFINITIONS).map((fusion) => (
                  <article key={fusion.id} className="codex-card" data-accent={fusion.id}>
                    <div
                      className="codex-card__symbol"
                      aria-hidden="true"
                      style={{
                        display: 'flex',
                        width: '4.7rem',
                        gap: '0.1rem',
                      }}
                    >
                      {fusion.requiredCoreIds.map((coreId) => (
                        <img
                          key={coreId}
                          src={CYBERPUNK_VISUAL_ASSETS.ballCores[coreId]}
                          alt=""
                          width={34}
                          height={34}
                          style={{
                            width: '2.1rem',
                            height: '2.1rem',
                            objectFit: 'contain',
                          }}
                        />
                      ))}
                    </div>
                    <p>CORE FUSION</p>
                    <h2>{fusion.name}</h2>
                    <span>{fusion.description}</span>
                    <div className="codex-tags">
                      {fusion.requiredCoreIds.map((coreId) => (
                        <small key={coreId}>◉ {BALL_CORE_DEFINITIONS[coreId].name}</small>
                      ))}
                    </div>
                  </article>
                )),
              ]
            : null}

          {category === 'builds'
            ? Object.values(BUILD_DEFINITIONS).map((build) => (
                <article key={build.id} className="codex-card" data-accent={build.id}>
                  <img
                    className="codex-card__image"
                    src={CYBERPUNK_VISUAL_ASSETS.builds[build.id]}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                  />
                  <p>STARTER DOCTRINE</p>
                  <h2>{build.name}</h2>
                  <span>{build.description}</span>
                  <div className="codex-tags">
                    {build.upgradeIds.map((upgradeId) => (
                      <small key={upgradeId}>{UPGRADE_DEFINITIONS[upgradeId].name}</small>
                    ))}
                  </div>
                </article>
              ))
            : null}

          {category === 'operations'
            ? OPERATION_ITEMS.map((item) => (
                <article key={item.id} className="codex-card" data-accent={item.id}>
                  <img
                    className="codex-card__image"
                    src={item.imageUrl}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                  />
                  <p>{item.label}</p>
                  <h2>{item.name}</h2>
                  <span>{item.description}</span>
                </article>
              ))
            : null}

          {category === 'relics'
            ? Object.values(RELIC_DEFINITIONS).map((relic) => (
                <article key={relic.id} className="codex-card" data-accent={relic.id}>
                  <img
                    className="codex-card__image"
                    src={CYBERPUNK_VISUAL_ASSETS.relics[relic.id]}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                  />
                  <p>RUN RELIC</p>
                  <h2>{relic.name}</h2>
                  <span>{relic.description}</span>
                  <small>击败 Boss 后从遗物奖励中获得</small>
                </article>
              ))
            : null}

          {category === 'abilities'
            ? Object.values(ACTIVE_ABILITY_DEFINITIONS).map((ability) => (
                <article key={ability.id} className="codex-card" data-accent={ability.id}>
                  <img
                    className="codex-card__image"
                    src={CYBERPUNK_VISUAL_ASSETS.abilities[ability.id]}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                  />
                  <p>ACTIVE ABILITY</p>
                  <h2>{ability.name}</h2>
                  <span>{ability.description}</span>
                  <small>冷却 {ability.cooldownMilliseconds / 1000} 秒</small>
                </article>
              ))
            : null}

          {category === 'boss'
            ? GAMEPLAY_CONFIG.boss.waves.map((wave) => {
                const encounter = getBossEncounterDefinition(wave);
                if (!encounter) {
                  return null;
                }
                return (
                  <article key={wave} className="codex-card codex-card--boss" data-boss-wave={wave}>
                    <img
                      className="codex-card__boss-image"
                      src={CYBERPUNK_VISUAL_ASSETS.gameplay.bosses[wave]}
                      alt=""
                      aria-hidden="true"
                    />
                    <p>
                      BOSS · LEVEL {wave} · HP {encounter.maxHitPoints}
                    </p>
                    <h2>{encounter.name}</h2>
                    <span>
                      {encounter.shield
                        ? '击破棱镜节点后，本体会短暂暴露。'
                        : encounter.summon
                          ? '优先清除蜂群增援，解除本体减伤。'
                          : '识别左右奇点预警，使用护盾压制牵引。'}
                    </span>
                    <div className="boss-phase-list">
                      {BOSS_DEFINITION.phases.map((phase) => (
                        <div key={phase.id}>
                          <strong>
                            0{phase.id} · {phase.name}
                          </strong>
                          <small>{phase.description}</small>
                        </div>
                      ))}
                    </div>
                  </article>
                );
              })
            : null}
        </section>
      </main>
    </div>
  );
}
